import React, { useCallback, useEffect, useState } from 'react';

import findLast from 'lodash/findLast';

import { AskAiRule } from '@lumapps/ask-ai-rules/types';
import { ASK_AI_CHAT_STREAMING_STATUS } from '@lumapps/ask-ai/constants';
import { useStreamAskAi } from '@lumapps/ask-ai/hooks/useStreamAskAi';

import { AskAiChatMessage } from '../types';
import { AskAiChatMessageWithoutID, generateMessageID } from '../utils';

interface UseAskAiConversationProps {
    initialQuery?: string;
    initialTraceId: string;
    conversationId: string;
    initialSelectedAgent?: AskAiRule;
}

export const useAskAiConversation = ({
    initialQuery,
    initialTraceId,
    conversationId,
    initialSelectedAgent,
}: UseAskAiConversationProps) => {
    const [query, setQuery] = useState<string>('');
    const [selectedAgent, setSelectedAgent] = React.useState<AskAiRule | undefined>(initialSelectedAgent);

    const { data, bestAnswer, refetch, isStreaming, isLoading, remove, stopStream } = useStreamAskAi(
        { query, conversationId, agentId: selectedAgent?.id },
        { enabled: !!query },
    );

    const [messages, setMessages] = useState<AskAiChatMessage[]>([]);

    const handleAddMessage = useCallback(
        (message: AskAiChatMessageWithoutID) =>
            setMessages((previousMessages) => [...previousMessages, generateMessageID(message)]),
        [],
    );

    /** Reset query when updating agent or conversationId  to avoid re-trigger Ask */
    const resetQuery = () => {
        setQuery('');
    };

    const handleRefreshConversation = () => {
        setMessages([]);
        resetQuery();
    };

    /** On `initialQuery` change, resets the conversation with a new user message and triggers Ask */
    useEffect(() => {
        const trimmedQuery = initialQuery?.trim();
        if (trimmedQuery) {
            setMessages([{ role: 'user', content: trimmedQuery, id: initialTraceId }]);
            setQuery(trimmedQuery);
        }
    }, [initialQuery, initialTraceId]);

    /** On Ask AI data reception, adds a message */
    useEffect(() => {
        if (data?.lastChunk?.data?.status === ASK_AI_CHAT_STREAMING_STATUS.DONE) {
            handleAddMessage({ role: 'agent', content: bestAnswer });
        }
    }, [data, handleAddMessage, bestAnswer]);

    /** On form submission, adds a user message and triggers Ask */
    const handleSubmit = (value?: string) => {
        if (isStreaming || isLoading) {
            return;
        }
        const trimmedValue = value?.trim();
        if (trimmedValue) {
            handleAddMessage({ role: 'user', content: trimmedValue });
            if (trimmedValue === query) {
                // Remove the query from the cache before fetching it another time
                remove();
                refetch();
            }
            setQuery(trimmedValue);
        }
    };

    /** On manual stream cancellation, add a message with the text already streamed */
    const handleCancelStream = () => {
        stopStream();
        if (bestAnswer.answer && bestAnswer.answer?.length > 0) {
            handleAddMessage({
                role: 'agent',
                content: { answer: bestAnswer.answer },
                manuallyCancelled: true,
            });
        }
    };

    /**
     * Handle agent change:
     * When the user changes the agent, we remove that assistant's
     * response and all subsequent messages, then ask the new agent the same question.
     */
    const handleChangeSelectedAgent = (assistantMessageId: string, newAgent?: AskAiRule) => {
        // Find the index of the specific assistant message
        const assistantIndex = messages.findIndex(
            (message) => message.role === 'agent' && message.id === assistantMessageId,
        );

        // If assistant message not found, keep all messages
        const previousMessages = assistantIndex > -1 ? messages.slice(0, assistantIndex) : messages;

        // Find the last user message related to this specific assistant message
        const userQuery = findLast(previousMessages, (message) => message.role === 'user');

        // Re-trigger the agent with the new query
        if (userQuery) {
            setQuery(userQuery.content);
        }

        setSelectedAgent(newAgent);
        setMessages(previousMessages);
    };

    return {
        onSubmit: handleSubmit,
        messages,
        bestAnswer,
        isStreaming,
        refetch,
        isLoading,
        handleRefreshConversation,
        resetQuery,
        conversationId: data?.lastChunk?.data?.conversationId,
        stopStream: handleCancelStream,
        data,
        handleChangeSelectedAgent,
        selectedAgent,
    };
};
